import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import useKidsStore from '../store/kidsStore';
import { mediaSrc } from '../api/media';

const MOODS: Record<string, string> = {
  happy: '😊', excited: '🤩', sad: '😢', tired: '😴', okay: '😐',
};

interface KidInfo { id: number; name: string; grade: number; username: string; }
interface SummaryInfo { date: string; summaryText: string; mood: string; }
interface HomeworkInfo { id: number; subject: string; title: string; status: string; dueDate: string; }
interface MathResultInfo { topicName: string; score: number; totalQuestions: number; percentage: number; attemptedAt: string; }
interface ReadingResultInfo { storyTitle: string; score: number; totalQuestions: number; percentage: number; attemptedAt: string; }

interface KidActivity {
  kid: KidInfo;
  recentSummaries: SummaryInfo[];
  homework: HomeworkInfo[];
  mathResults: MathResultInfo[];
  readingResults: ReadingResultInfo[];
}

interface RetentionStatus {
  expiringCount: number;
  earliestExpiry: string | null;
  extendedUntil: string | null;
}

const pctColor = (p: number) =>
  p >= 80 ? 'text-green-600' : p >= 60 ? 'text-yellow-600' : 'text-red-500';

const pctBg = (p: number) =>
  p >= 80 ? 'bg-green-100 text-green-700' : p >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600';

const fmt = (dt: string) =>
  new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const fmtTime = (dt: string) =>
  new Date(dt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

type Tab = 'summary' | 'homework' | 'math' | 'reading';

const KidActivity: React.FC = () => {
  const { kidId } = useParams<{ kidId: string }>();
  const navigate = useNavigate();
  const { fetchKids } = useKidsStore();
  const [activity, setActivity] = useState<KidActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('summary');
  const [retention, setRetention] = useState<RetentionStatus | null>(null);
  const [extending, setExtending] = useState(false);
  const [extendSuccess, setExtendSuccess] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');

  useEffect(() => {
    Promise.all([
      api.get(`/api/parent/kids/${kidId}/activity`),
      api.get(`/api/parent/kids/${kidId}/retention-status`),
    ])
      .then(([actRes, retRes]) => {
        setActivity(actRes.data);
        setRetention(retRes.data);
      })
      .catch(() => navigate('/parent/dashboard'))
      .finally(() => setLoading(false));
    api.get(`/api/kids/${kidId}/profile-photo`).then((r) => setPhotoUrl(r.data?.url || null)).catch(() => {});
  }, [kidId, navigate]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post(`/api/parent/kids/${kidId}/profile-photo`, fd);
      setPhotoUrl(r.data?.url || null);
      fetchKids(); // refresh sidebar avatar
    } catch { alert('Failed to upload photo'); }
    finally { e.target.value = ''; }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg('');
    setPwSaving(true);
    try {
      await api.post(`/api/parent/kids/${kidId}/password`, { password: newPassword });
      setNewPassword('');
      setPwMsg('✓ Password updated');
      setTimeout(() => setPwMsg(''), 2500);
    } catch {
      setPwMsg('Failed to update password');
    } finally {
      setPwSaving(false);
    }
  };

  const handleRemoveKid = async () => {
    if (!window.confirm("Remove this kid? This deletes all their data and can't be undone.")) return;
    try {
      await api.delete(`/api/parent/kids/${kidId}`);
      await fetchKids();
      navigate('/parent/dashboard');
    } catch {
      alert('Failed to remove kid');
    }
  };

  const handleExtendRetention = async () => {
    setExtending(true);
    try {
      const res = await api.post(`/api/parent/kids/${kidId}/extend-retention`);
      setRetention(res.data);
      setExtendSuccess(true);
      setTimeout(() => setExtendSuccess(false), 3000);
    } catch {
      alert('Failed to extend retention. Please try again.');
    } finally {
      setExtending(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-gray-500">Loading activity...</p>
    </div>
  );

  if (!activity) return null;

  const { kid, recentSummaries, homework, mathResults, readingResults } = activity;
  const hwDone = homework.filter(h => h.status === 'DONE').length;
  const hwPending = homework.filter(h => h.status === 'PENDING').length;
  const avgMath = mathResults.length
    ? Math.round(mathResults.reduce((s, r) => s + r.percentage, 0) / mathResults.length)
    : null;
  const avgReading = readingResults.length
    ? Math.round(readingResults.reduce((s, r) => s + r.percentage, 0) / readingResults.length)
    : null;

  const tabs: { key: Tab; label: string; emoji: string }[] = [
    { key: 'summary', label: 'Summaries', emoji: '📝' },
    { key: 'homework', label: 'Homework', emoji: '📚' },
    { key: 'math', label: 'Math', emoji: '🧮' },
    { key: 'reading', label: 'Reading', emoji: '📖' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-2xl mx-auto p-6 space-y-5">
        <div className="flex justify-between items-start gap-4">
          <div className="flex items-center gap-3">
            {photoUrl ? (
              <img src={mediaSrc(photoUrl)} alt={kid.name}
                   className="w-14 h-14 rounded-full object-cover border border-purple-200" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center text-2xl">🧒</div>
            )}
            <div>
              <h1 className="text-xl font-bold text-purple-600">{kid.name}'s Activity</h1>
              <p className="text-xs text-gray-400">Grade {kid.grade} · @{kid.username}</p>
            </div>
          </div>
          <button onClick={handleRemoveKid}
            className="text-xs text-red-400 hover:text-red-600 border border-red-200 hover:border-red-400 rounded-lg px-3 py-1.5 shrink-0">
            Remove kid
          </button>
        </div>

        {/* Settings: profile photo + password */}
        <div className="bg-white rounded-2xl shadow-sm p-5 grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Profile photo</p>
            <label className="inline-flex items-center gap-2 text-sm text-parentaccent border border-dashed border-indigo-300 bg-indigo-50 hover:bg-indigo-100 rounded-lg px-3 py-2 cursor-pointer transition">
              📷 Choose photo
              <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
            </label>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Change password</p>
            <form onSubmit={handleChangePassword} className="flex gap-2">
              <input type="password" required value={newPassword} minLength={3}
                onChange={(e) => setNewPassword(e.target.value)} placeholder="New password"
                className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
              <button type="submit" disabled={pwSaving}
                className="bg-parentaccent hover:opacity-90 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50">
                {pwSaving ? '…' : 'Save'}
              </button>
            </form>
            {pwMsg && <p className={`text-xs mt-1 ${pwMsg.startsWith('✓') ? 'text-green-600' : 'text-red-500'}`}>{pwMsg}</p>}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white rounded-xl shadow-sm p-3 text-center">
            <p className="text-2xl font-bold text-orange-500">{recentSummaries.length}</p>
            <p className="text-xs text-gray-500 mt-1">Summaries</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3 text-center">
            <p className="text-2xl font-bold text-green-500">{hwDone}</p>
            <p className="text-xs text-gray-500 mt-1">HW Done</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3 text-center">
            <p className={`text-2xl font-bold ${avgMath !== null ? pctColor(avgMath) : 'text-gray-400'}`}>
              {avgMath !== null ? `${avgMath}%` : '—'}
            </p>
            <p className="text-xs text-gray-500 mt-1">Avg Math</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3 text-center">
            <p className={`text-2xl font-bold ${avgReading !== null ? pctColor(avgReading) : 'text-gray-400'}`}>
              {avgReading !== null ? `${avgReading}%` : '—'}
            </p>
            <p className="text-xs text-gray-500 mt-1">Avg Reading</p>
          </div>
        </div>

        {/* Retention warning banner */}
        {retention && retention.expiringCount > 0 && !retention.extendedUntil && (
          <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 flex items-start justify-between gap-3">
            <div className="flex gap-2 items-start">
              <span className="text-yellow-500 text-lg mt-0.5">⚠️</span>
              <div>
                <p className="text-sm font-semibold text-yellow-800">Quiz results expiring soon</p>
                <p className="text-sm text-yellow-700 mt-0.5">
                  <strong>{retention.expiringCount} quiz result{retention.expiringCount > 1 ? 's' : ''}</strong>
                  {retention.earliestExpiry && (
                    <> will be deleted on <strong>{fmt(retention.earliestExpiry)}</strong></>
                  )}. Extend to keep them for 3 more months.
                </p>
              </div>
            </div>
            <button
              onClick={handleExtendRetention}
              disabled={extending}
              className="shrink-0 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-3 py-1.5 rounded-lg disabled:opacity-50 transition"
            >
              {extending ? 'Extending...' : 'Extend 3 months'}
            </button>
          </div>
        )}

        {/* Extended confirmation */}
        {retention && retention.extendedUntil && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
            <span className="text-green-500">✅</span>
            <p className="text-sm text-green-700">
              Quiz results retained until <strong>{fmt(retention.extendedUntil)}</strong>.
              {extendSuccess && ' Extended successfully!'}
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex bg-white rounded-xl shadow-sm p-1 gap-1">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                tab === t.key
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.emoji} {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}

        {/* Daily Summaries */}
        {tab === 'summary' && (
          <div className="space-y-3">
            {recentSummaries.length === 0 ? (
              <Empty message="No summaries written yet." />
            ) : (
              recentSummaries.map((s, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-600">{fmt(s.date)}</span>
                    <span className="text-xl">{MOODS[s.mood] || '😐'}</span>
                  </div>
                  <p className="text-sm text-gray-600">{s.summaryText}</p>
                </div>
              ))
            )}
          </div>
        )}

        {/* Homework */}
        {tab === 'homework' && (
          <div className="space-y-3">
            {/* Status bar */}
            <div className="bg-white rounded-xl shadow-sm p-4 flex gap-6">
              <div className="flex items-center gap-2">
                <span className="text-green-500 text-lg">✅</span>
                <span className="text-sm font-medium text-gray-700">{hwDone} completed</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-yellow-500 text-lg">⏳</span>
                <span className="text-sm font-medium text-gray-700">{hwPending} pending</span>
              </div>
            </div>
            {homework.length === 0 ? (
              <Empty message="No homework added yet." />
            ) : (
              homework.map(h => (
                <div key={h.id} className="bg-white rounded-xl shadow-sm p-4 flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{h.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{h.subject} · Due {fmt(h.dueDate)}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    h.status === 'DONE'
                      ? 'bg-green-100 text-green-700'
                      : h.status === 'IN_PROGRESS'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {h.status === 'DONE' ? '✓ Done' : h.status === 'IN_PROGRESS' ? 'In Progress' : 'Pending'}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {/* Math Results */}
        {tab === 'math' && (
          <div className="space-y-3">
            {mathResults.length === 0 ? (
              <Empty message="No math quizzes attempted yet." />
            ) : (
              mathResults.map((r, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm p-4 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{r.topicName}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{fmtTime(r.attemptedAt)}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-bold px-3 py-1 rounded-full ${pctBg(r.percentage)}`}>
                      {Math.round(r.percentage)}%
                    </span>
                    <p className="text-xs text-gray-400 mt-1">{r.score}/{r.totalQuestions}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Reading Results */}
        {tab === 'reading' && (
          <div className="space-y-3">
            {readingResults.length === 0 ? (
              <Empty message="No reading quizzes attempted yet." />
            ) : (
              readingResults.map((r, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm p-4 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{r.storyTitle}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{fmtTime(r.attemptedAt)}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-bold px-3 py-1 rounded-full ${pctBg(r.percentage)}`}>
                      {Math.round(r.percentage)}%
                    </span>
                    <p className="text-xs text-gray-400 mt-1">{r.score}/{r.totalQuestions}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

      </main>
    </div>
  );
};

const Empty: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
    <p>{message}</p>
  </div>
);

export default KidActivity;
